import {useContext, useEffect, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {TablePagination} from "./ReportingLab.jsx";

function formatDate(value) {
    return value
        ? new Intl.DateTimeFormat("cs-CZ", {dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Prague"}).format(new Date(value))
        : "—";
}

function formatCreatedAt(value) {
    if (!value) return {date: "—", time: ""};

    const date = new Date(value);
    return {
        date: new Intl.DateTimeFormat("cs-CZ", {dateStyle: "medium", timeZone: "Europe/Prague"}).format(date),
        time: new Intl.DateTimeFormat("cs-CZ", {timeStyle: "short", timeZone: "Europe/Prague"}).format(date),
    };
}

function subscriptionStatusLabel(status, t) {
    const labels = {
        active: t("admin.projects.subscription_active", {defaultValue: "Aktivní"}),
        pending: t("admin.projects.subscription_pending", {defaultValue: "Čeká na aktivaci"}),
        past_due: t("admin.projects.subscription_past_due", {defaultValue: "Po splatnosti"}),
        canceled: t("admin.projects.subscription_canceled", {defaultValue: "Zrušené"}),
        expired: t("admin.projects.subscription_expired", {defaultValue: "Vypršelo"}),
    };
    return labels[status] ?? status;
}

function integrationLabel(name) {
    const labels = {
        Meta: "Meta Ads", Google: "Google Ads", Microsoft: "Microsoft Advertising",
        MySQLLead: "MySQL Leads", BsShop: "BSSHOP", Shoptet: "Shoptet", Sklik: "Sklik",
        Heureka: "Heureka", Upgates: "Upgates", Glami: "GLAMI", Zbozi: "Zboží.cz",
        UpgatesAddon: "Upgates doplněk", ShoptetAddon: "Shoptet doplněk",
        GoogleMerchant: "Google Merchant", GoogleAnalytics: "Google Analytics",
        GoogleSearchConsole: "Google Search Console", GoogleBigQuery: "Google BigQuery",
        Pinterest: "Pinterest Ads", TikTok: "TikTok Ads", Criteo: "Criteo Ads",
    };
    return labels[name] ?? name;
}

export default function AdminProjects() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {setCurrentProjectId} = useContext(GlobalContext);
    const configuration = useAuthorityConfiguration();
    const {et: translateError} = useErrorTranslation();
    const dependenciesRef = useRef({configuration, translateError, t});
    dependenciesRef.current = {configuration, translateError, t};
    const [data, setData] = useState({items: [], total: 0});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => setPage(1), [debouncedSearch, pageSize]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const {configuration: currentConfiguration} = dependenciesRef.current;
        new AdministrationApi(currentConfiguration).listAdminProjects({
            page,
            pageSize,
            search: debouncedSearch || undefined,
        }).then((result) => {
            if (!cancelled) setData({items: result.items ?? [], total: result.total ?? 0});
        }).catch(async (error) => {
            const {translateError: translateCurrentError, t: translate} = dependenciesRef.current;
            if (!cancelled) toast.error(await translateCurrentError(error) || translate("admin.projects.load_failed", {defaultValue: "Projekty se nepodařilo načíst."}));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [page, pageSize, debouncedSearch]);

    return <WideSecureLayout title={t("admin.projects.title", {defaultValue: "Projekty"})}>
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
                {t("admin.projects.description", {defaultValue: "Nejnovější projekty a jejich kontaktní osoby. Hlavní kontakt odpovídá současnému vlastníkovi projektu."})}
            </p>
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="border-b border-slate-200 p-4 dark:border-slate-700">
                    <input
                        className="form-input w-full md:max-w-xl"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={t("admin.projects.search", {defaultValue: "Projekt, jméno, e-mail nebo telefon…"})}
                    />
                </div>
                <div className="relative overflow-x-auto">
                    {loading && <LoadingComponent/>}
                    <table className="w-full min-w-[1450px] text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
                        <tr>
                            <th className="px-4 py-3">{t("admin.projects.created", {defaultValue: "Vytvořeno"})}</th>
                            <th className="px-4 py-3">{t("admin.projects.project", {defaultValue: "Projekt"})}</th>
                            <th className="px-4 py-3">{t("admin.projects.subscription", {defaultValue: "Předplatné"})}</th>
                            <th className="px-4 py-3">{t("admin.projects.integrations", {defaultValue: "Aktivní integrace"})}</th>
                            <th className="px-4 py-3">{t("admin.projects.contacts", {defaultValue: "Uživatelé a kontakty"})}</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {(data.items ?? []).map((project) => {
                            const createdAt = formatCreatedAt(project.createdAt);
                            return <tr key={project.id} className="align-top odd:bg-white even:bg-slate-50/70 hover:bg-violet-50/70 dark:odd:bg-slate-800 dark:even:bg-slate-900/35 dark:hover:bg-violet-950/30">
                            <td className="whitespace-nowrap px-4 py-4">
                                <div className="font-semibold text-slate-900 dark:text-white">{createdAt.date}</div>
                                {createdAt.time && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{createdAt.time}</div>}
                            </td>
                            <td className="px-4 py-4">
                                <button
                                    type="button"
                                    className="font-semibold text-slate-900 underline-offset-4 hover:text-violet-600 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:text-white dark:hover:text-violet-300"
                                    title={t("admin.projects.activate", {name: project.name, defaultValue: "Aktivovat projekt {{name}}"})}
                                    onClick={() => {
                                        setCurrentProjectId(project.id);
                                        navigate("/");
                                    }}
                                >
                                    {project.name}
                                </button>
                                <div className="mt-1 font-mono text-xs text-slate-400">{project.id}</div>
                            </td>
                            <td className="px-4 py-4">
                                {project.subscriptionPackageName ? <>
                                    <div className="font-semibold text-slate-900 dark:text-white">{project.subscriptionPackageName}</div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {project.isTrial && <Badge color="blue">{t("admin.projects.trial", {defaultValue: "Trial"})}</Badge>}
                                        <Badge color={project.subscriptionStatus === "active" ? "emerald" : project.subscriptionStatus === "past_due" ? "rose" : "slate"}>
                                            {subscriptionStatusLabel(project.subscriptionStatus, t)}
                                        </Badge>
                                    </div>
                                    {project.subscriptionEndsAt && <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("admin.projects.subscription_until", {defaultValue: "Do"})}: {formatDate(project.subscriptionEndsAt)}</div>}
                                </> : <Badge color="slate">{t("admin.projects.no_subscription", {defaultValue: "Bez předplatného"})}</Badge>}
                            </td>
                            <td className="px-4 py-4">
                                <div className="mb-2 font-semibold text-slate-900 dark:text-white">
                                    {t("admin.projects.integration_count", {count: project.activeIntegrations?.length ?? 0, defaultValue: "{{count}} aktivních"})}
                                </div>
                                <div className="flex max-w-md flex-wrap gap-1.5">
                                    {(project.activeIntegrations ?? []).map((integration) => <Badge key={integration} color="violet">{integrationLabel(integration)}</Badge>)}
                                    {(project.activeIntegrations ?? []).length === 0 && <span className="text-slate-400">{t("admin.projects.no_integrations", {defaultValue: "Žádná"})}</span>}
                                </div>
                            </td>
                            <td className="px-4 py-4">
                                <div className="grid gap-2 xl:grid-cols-2">
                                    {(project.users ?? []).map((user) => <div key={user.id} className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-semibold text-slate-900 dark:text-white">{user.name}</span>
                                            {user.isPrimaryContact && <Badge color="violet">{t("admin.projects.primary_contact", {defaultValue: "Hlavní kontakt"})}</Badge>}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                            <a className="text-violet-600 hover:underline dark:text-violet-300" href={`mailto:${user.email}`}>{user.email}</a>
                                            {user.phone && <a className="text-violet-600 hover:underline dark:text-violet-300" href={`tel:${user.phone}`}>{user.phone}</a>}
                                        </div>
                                    </div>)}
                                    {(project.users ?? []).length === 0 && <span className="text-slate-400">{t("admin.projects.no_contacts", {defaultValue: "Bez kontaktního uživatele"})}</span>}
                                </div>
                            </td>
                        </tr>;
                        })}
                        {!loading && (data.items ?? []).length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">{t("admin.projects.empty", {defaultValue: "Filtru neodpovídá žádný projekt."})}</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 pb-4">
                    <TablePagination page={page} pageSize={pageSize} totalItems={data.total ?? 0} onPageChange={setPage} onPageSizeChange={setPageSize} t={t}/>
                </div>
            </section>
        </div>
    </WideSecureLayout>;
}
